/**
 * CancelJobButton
 *
 * Destructive button rendered inside an active job modal.
 * Disabled when the job is already in a terminal state.
 * Opens CancelJobConfirmDialog on click; calls onAborted when the
 * abort request resolves.
 *
 * Props:
 *   jobScope  — which subsystem owns this job
 *   jobId     — the job ID to abort
 *   isActive  — true while the job is queued or running (button is enabled)
 *   onAborted — called with the resolved terminal status after abort succeeds
 */

import { useCallback, useRef, useState } from "react";

import { Button } from "../ui/Button.js";
import { Icon } from "../ui/Icon.js";
import { toast } from "../../lib/toast.js";
import { cancelJob, CancelJobError } from "./cancelJob.js";
import { CancelJobConfirmDialog } from "./CancelJobConfirmDialog.js";
import type { JobScope, CancelJobBody } from "./cancelJob.js";

// ── Types ─────────────────────────────────────────────────────────────────────

type CancelJobButtonProps = {
  jobScope: JobScope;
  jobId: string;
  isActive: boolean;
  onAborted: (status: string) => void;
};

// ── Component ─────────────────────────────────────────────────────────────────

export const CancelJobButton = ({
  jobScope,
  jobId,
  isActive,
  onAborted,
}: CancelJobButtonProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Keep a ref so the abort can be initiated even if props changed mid-flight
  const jobIdRef = useRef(jobId);
  jobIdRef.current = jobId;
  const jobScopeRef = useRef(jobScope);
  jobScopeRef.current = jobScope;

  // Capture the trigger wrapper so focus can return after dialog closes
  const triggerWrapperRef = useRef<HTMLSpanElement>(null);

  const handleOpen = useCallback(() => {
    setError(null);
    setDialogOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    if (loading) return;
    setDialogOpen(false);
    setError(null);
    // Return focus to the trigger button after the portal unmounts
    window.setTimeout(() => {
      const btn = triggerWrapperRef.current?.querySelector<HTMLButtonElement>("button");
      btn?.focus();
    }, 0);
  }, [loading]);

  const handleConfirm = useCallback(async (body: CancelJobBody) => {
    setLoading(true);
    setError(null);

    try {
      const result = await cancelJob(jobScopeRef.current, jobIdRef.current, body);
      setDialogOpen(false);

      if (body.report) {
        toast.success("Job cancelled — report sent to admins");
      } else {
        toast.success("Job cancelled");
      }

      onAborted(result.status);
    } catch (err) {
      if (err instanceof CancelJobError) {
        setError(err.message);
      } else {
        setError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [onAborted]);

  return (
    <>
      <span ref={triggerWrapperRef}>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={handleOpen}
          disabled={!isActive}
          className="text-red-400 hover:text-red-300 hover:bg-red-500/10 disabled:opacity-0 transition-opacity"
          aria-label="Cancel this job"
        >
          <Icon icon="lucide:square" className="h-3.5 w-3.5" />
          Cancel job
        </Button>
      </span>

      <CancelJobConfirmDialog
        open={dialogOpen}
        scope={jobScope}
        loading={loading}
        error={error}
        onClose={handleClose}
        onConfirm={(body) => void handleConfirm(body)}
      />
    </>
  );
};
